import { Document, ObjectId, Schema } from 'mongoose';
import { Country } from '../Countries';
import { Network } from '../Network';
import { MondayStatus, MondayRejectionReason } from '../../../routes/types/Monday';
import { S3FieldType } from './S3FieldType';
import { ApplePublishingStatus } from '@/server/routes/types/Apple';

export type S3Object = {
  filename: string;
  filetype: string;
  originalname: string;
  size: number;
  url: string;
  path: string;
};

export type BaseShow = {
  title: string;
  subtitle: string;
  showId: string;
  alias?: string;
  network: ObjectId;
  ownerEmail: string;
  obfuscateEmail: boolean;
  author: string;
  ownerName: string;
  summary: string;
  lang: string;
  creationDate: Date;
  categorie1: string;
  categorie2: string;
  categorie3: string;
  keywords: string;
  link: string;
  lastEditedDate: Date;
  feedLimit?: number;
  copyright: string;
  redirect?: boolean;
  redirectUrl?: string;
};

interface PopulatedRegionlock extends Document {
  regionLock: boolean;
  allowedByDefault: boolean;
  list: Pick<Country, 'value'>[];
}

export interface IShow extends BaseShow, Document {
  disabled?: boolean;
  deletedDate?: Date | null;
  owner: ObjectId;
  createImport: boolean;
  block: boolean;
  publishDate: Date;
  source: string;
  explicit: boolean;
  type: string;
  cover: S3Object;
  status: string;
  isHosted: boolean;
  isPrivate: boolean;
  accessToken: string;
  itunesId?: string;
  acastLink: string;
  pingbackReceiver: string;
  spotifyUri?: string;
  deezerUri?: string;
  feederId: string;
  domain: string;
  keywords: string; // should ideally be string[];
  midrollDurationThreshold: number;
  secondMidrollThreshold: number;
  canUseSecondMidroll: boolean;
  adFreeEnabled: boolean;
  adFreeToken: string;
  integrations: {
    feeder: {
      id: string;
    };
    supporter: {
      isEnabled: boolean;
      descriptionText: string;
      descriptionLink: string;
    };
    amazon: {
      isEnabled: boolean;
      submitted: Date;
    };
    webhooks: {
      isEnabled: boolean;
      url: string;
    };
    access: {
      isEnabled: boolean;
      feedUrl: string;
      token: string;
      embedToken: string;
    };
    customPaywall: {
      privateTitle: string;
      privateDescription: string;
      privateImage: S3Object;
      isEnabled: boolean;
    };
    apple?: {
      id?: string;
      status?: ApplePublishingStatus;
      claimToken?: string;
      rejectionReason?: string;
    };
  };
  acastSettings: {
    adInSound: string;
    adOutSound: string;
    intro: string;
    outro: string;
    regionLock: PopulatedRegionlock | undefined;
  };
  accessTokens: {
    token: string;
    tiers: string[];
  }[];
  publicTiers: {
    id: string;
    name: string;
  }[];
  website: {
    title: string;
    subtitle: string;
    hosts: {
      name: {
        first: string;
        last: string;
      };
      title: string;
      bio: string;
      profilePicture: S3Object;
      twitter: string;
      facebook: string;
      instagram: string;
      linkedin: string;
      tiktok: string;
    }[];
    podcastImage: S3Object;
    defaultColor: string;
    primaryColor: string;
    secondaryColor: string;
    theme: string;
    fontHeading: string;
    fontBody: string;
    cornerStyle: string;
    facebookPixelId: string;
    googleAnalyticsId: string;
    facebookAppId: string;
    disqusUrl: string;
    commentSelected: string;
    facebookId: string;
    patreonId: string;
    itunesId: string;
    amazonMusicId: string;
    twitterId: string;
    instagramId: string;
    tiktokId: string;
    storeUrl: string;
    notes: string;
    externalLinks: {
      url: string;
      name: string;
    }[];
    enabledPlayerLinks: string[];
    customPlayerLinks: {
      url: string;
      name: string;
    }[];
    episodeTrailer: string;
    headerCover: {
      image: S3Object;
      overlay: string;
      opacity: number;
      blur: number;
    };
  };
  import: {
    numOfEpisodes: number;
  };
  marketplace: {
    status: MondayStatus;
    itemId: string;
    rejectedReason?: MondayRejectionReason | undefined;
    approvedDate?: Date | undefined;
    rejectedDate?: Date | undefined;
    isMidrollAutoplacement: boolean;
    country?: string;
  };
  canAccessMarketplaceProfile: boolean;
  canSubscribeToMarketplace: boolean;
  globalEpisodeDescription: {
    enabled: boolean;
    text: string;
  };
  canUseAIfeatures: boolean;
  canUsePhyllo: boolean;
  // Virtual
  coverCdnUrl: string;
  feedUrl: string;
  // isPodtoppen flag defines if show should be exported to norway ranker podtoppen.no
  isPodtoppen: boolean;
  // Temporary property for runtime use, not persisted in MongoDB
  _modifiedPaths?: string[];
}

export interface ShowWithNetwork extends Omit<IShow, 'network'> {
  network: Network;
}

export interface ShowWithOwner extends Omit<IShow, 'owner'> {
  owner: User;
}

export enum AdMarkerPlacement {
  PREROLL = 'preroll',
  MIDROLL = 'midroll',
  POSTROLL = 'postroll',
}

export const ALLOWED_MARKER_PLACEMENTS = [
  AdMarkerPlacement.PREROLL,
  AdMarkerPlacement.MIDROLL,
  AdMarkerPlacement.POSTROLL,
] as const;

export type AdMarker = {
  start: number;
  placement: (typeof ALLOWED_MARKER_PLACEMENTS)[number];
};

export interface Episode extends Document, EpisodeMethods {
  episodeId: string;
  title: string;
  alias: string;
  link: string;
  show: ObjectId;
  owner: ObjectId;
  creationDate: Date;
  deletedDate?: Date | null;
  disabled?: boolean;
  lastEditedDate: Date;
  guid: string;
  publishDate: Date;
  subtitle: string;
  summary: string;
  season: number;
  episodeNumber: number;
  type: 'full' | 'trailer' | 'bonus';
  explicit: boolean;
  audio: S3Object;
  overrideAudioUrl: string;
  duration: number;
  cover: S3Object;
  publicIntro: S3Object;
  publicOutro: S3Object;
  privateIntro: S3Object;
  privateOutro: S3Object;
  status: 'disabled' | 'processing' | 'draft' | 'published' | 'scheduled' | 'importing' | 'failed';
  errorType: string;
  feederId: string;
  encoding?: {
    id: string;
    status: string;
    completionDate?: Date;
  };
  enableMonetization: boolean;
  transcript: {
    location: string;
    jobId: string;
    status: 'processing' | 'created' | 'failed';
  };
  acastTranscript?: {
    jobId: string;
    status: 'processing' | 'completed' | 'failed';
    error?: string;
    language?: string;
    creationDate?: Date;
    transcriptLocation?: string;
  };
  integrations: {
    acastPlus: {
      tiers: string[];
    };
    public: {
      tiers: string[];
    };
    customPaywall: {
      locked: boolean;
    };
  };

  copyrights: {
    isBlocked: boolean;
  };
  markers: AdMarker[];

  publishSequence: {
    date: Date;
    integrations: {
      acastPlus: string[];
      public: string[];
    };
    done: boolean;
  }[];
  isImported?: boolean;
  import: {
    resourceId: string;
    error: string;
  };

  // Temporary property for runtime use, not persisted in MongoDB
  _modifiedPaths?: string[];
}

/**
 * The `EpisodeMethods` interface defines instance methods for the `Episode` schema,
 * such as `notifyIntegrations`.
 * By extending `EpisodeMethods`, we ensure that all instance methods
 * are properly typed and available when using `Episode` documents.
 */
export interface EpisodeMethods {
  notifyIntegrations: (payload?: {
    trigger_all?: boolean;
    trigger_webhook?: boolean;
  }) => Promise<[any]>;
}

export interface EpisodeWithShow extends Omit<Episode, 'show'> {
  show: IShow;
}

export type BaseMarketplaceProfile = {
  _id: ObjectId;
  show: ObjectId;
  socialMediaLinks?: SocialMediaLinks;
  phyllo?: {
    sdkToken?: string;
    phylloUserId?: string;
    expiresAt?: Date;
  };
  creationDate?: Date;
  deletedDate?: Date | null;
  lastEditedDate?: Date;
};

export type SocialMediaLinks = {
  facebook?: SocialMediaPlatform;
  instagram?: SocialMediaPlatform;
  tiktok?: SocialMediaPlatform;
  youtube?: SocialMediaPlatform;
  linkedin?: SocialMediaPlatform;
  customLinks?: CustomLinks[];
};

export type SocialMediaPlatform = {
  id?: string;
  isCampaignInclude?: boolean;
  phyllo?: {
    accountId?: string;
    status: string;
    platformProfileName?: string;
    profilePicUrl?: string;
    workPlatformId: string;
    tokenUpdatedDate: Date;
  };
};

export type CustomLinks = {
  isCampaignInclude?: boolean;
  url?: string;
};

export type AIGeneratedContent = {
  titles: string[];
  description: string;
};

export type AIResponseCache = {
  key: string;
  show: ObjectId;
  content: AIGeneratedContent;
  creationDate: Date;
};

export interface User extends Document<string> {
  userId: string;
  createImport: boolean;
  name: {
    first: string;
    last: string;
  };
  email: string;
  status: string;
  creationDate: Date;
  lastEditedDate: Date;
  maxUploadFileSize?: number;
  crowdinWidget?: boolean;
  creationIp?: string;
  isAdmin: boolean;
  isPodcaster: boolean;
  isTeamMember: boolean;
  subscription: {
    customerId?: string;
  };
  flags: {
    manageUsers?: boolean;
    integrations?: boolean;
    hideLogo?: boolean;
    amazon?: boolean;
    translator?: boolean;
    editEpisodesTableSettings?: boolean;
    disableShowQuota?: boolean;
  };
  api: {
    key?: string;
    status?: string;
  };
  integrations: {
    headliner?: {
      isEnabled: boolean;
    };
  };
  notifications?: {
    show: ObjectId;
    scheduledEpisodePublished: boolean;
    integrationsUpdated: boolean;
  }[];

  updateEmailIfChanged(email: string): Promise<boolean>;
}

export type RestApiUser = {
  userId: string;
  email: string;
  api: {
    key?: string;
    status?: string;
  };
};

export interface MarketplaceHostProfile extends BaseMarketplaceProfile {
  profilePicture?: typeof S3FieldType;
  name: string;
  bio?: string;
  interests?: string[];
  location?: {
    country?: string;
    city?: string;
  };
  specificData?: {
    isChecked?: boolean;
    gender?: string;
    genderIdentity?: string;
    ethnicity?: string;
    religion?: string;
    sexuality?: string;
  };
  occupation?: string;
  homeOwner?: string;
  kidsBirthDate?: string[];
  petsInfo?: string[];
  preferredBrands?: string[];
}

export interface MarketplacePodcastProfile extends BaseMarketplaceProfile {
  sponsorshipReaders: string[];
  seasonalThemesContent?: string[];
}

export interface TranscriptionMetadata extends Document {
  show: { type: Schema.Types.ObjectId; ref: 'Show'; required: true; index: true };
  creationDate: Date;
  audioUrl: string;
  jobId: string;
  sourceLocation?: string;
  transcriptLocation?: string;
  status: 'processing' | 'completed' | 'failed';
  error?: string;
  language?: string;
  duration?: number;
}
